import React, { useEffect, useMemo, useState } from "react";
import { api, apiError } from "../lib/api";
import { toast } from "sonner";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  CartesianGrid,
} from "recharts";

// Human-readable labels for event_type strings. Every event we emit on the
// backend gets a label here; unknown types fall through as raw strings.
export const EVENT_LABELS = {
  login: "Logins",
  profile_created: "Profiles created",
  profile_bulk_imported: "CSV imports",
  calibration_started: "Calibrations started",
  calibration_manager_submitted: "Manager submitted",
  calibration_employee_submitted: "Employee submitted",
  calibration_completed: "Calibrations completed",
  pd_generated: "PDs generated",
  pd_updated: "PDs edited",
  pd_exported_docx: "PDs exported (.docx)",
  pd_deleted: "PDs deleted",
  plan_upgraded: "Plan upgrades",
};

// Recharts palette — vivid enough to distinguish 6+ series on one chart.
// Order matters: earliest events get the strongest colours.
const SERIES_COLORS = [
  "#FF4F00", "#0A84FF", "#22C55E", "#A855F7",
  "#EAB308", "#EC4899", "#14B8A6", "#F97316",
  "#6366F1", "#84CC16", "#DC2626", "#0EA5E9",
];

const RANGES = [
  { key: "7d", label: "7 days" },
  { key: "30d", label: "30 days" },
  { key: "90d", label: "90 days" },
  { key: "12m", label: "12 months" },
];

const formatN = (n) => (n == null ? "—" : Number(n).toLocaleString());
const fmtDate = (s) => { if (!s) return "—"; try { return new Date(s).toLocaleString(); } catch { return s; } };

function StatCard({ label, value, sublabel, testid }) {
  return (
    <div className="cal-card p-4" data-testid={testid}>
      <div className="font-mono-cal text-[10px] uppercase tracking-widest text-[#666] mb-2">
        [ {label} ]
      </div>
      <div className="font-display font-bold text-3xl leading-none">{formatN(value)}</div>
      {sublabel && <div className="text-xs text-[#666] mt-2">{sublabel}</div>}
    </div>
  );
}

/**
 * Usage analytics dashboard block. Works for both platform-owner ("platform"
 * scope, cross-org) and org-admin ("org" scope, current-org-only) views.
 *
 * Props:
 *   - scope: "platform" | "org"
 */
export default function UsageAnalytics({ scope = "platform" }) {
  const [range, setRange] = useState("30d");
  const [overview, setOverview] = useState(null);
  const [series, setSeries] = useState([]);
  const [topUsers, setTopUsers] = useState([]);
  const [topOrgs, setTopOrgs] = useState([]);
  const [recent, setRecent] = useState([]);
  const [inactive, setInactive] = useState([]);
  const [loading, setLoading] = useState(true);

  const isPlatform = scope === "platform";
  const basePath = isPlatform ? "/admin/analytics" : "/org/analytics";

  const load = async () => {
    setLoading(true);
    try {
      const jobs = [
        api.get(`${basePath}/overview`, { params: { range } }),
        api.get(`${basePath}/timeseries`, { params: { range } }),
        api.get(`${basePath}/top-users`, { params: { range, limit: 10 } }),
      ];
      if (isPlatform) {
        jobs.push(api.get("/admin/analytics/top-orgs", { params: { range, limit: 10 } }));
        jobs.push(api.get("/admin/analytics/recent-events", { params: { limit: 30 } }));
        jobs.push(api.get("/admin/analytics/inactive-orgs", { params: { days: 30 } }));
      }
      const results = await Promise.all(jobs);
      setOverview(results[0].data);
      setSeries(results[1].data?.series || []);
      setTopUsers(results[2].data?.users || []);
      if (isPlatform) {
        setTopOrgs(results[3].data?.orgs || []);
        setRecent(results[4].data?.events || []);
        setInactive(results[5].data?.orgs || []);
      }
    } catch (e) {
      toast.error(apiError(e, "Failed to load analytics"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [range]);

  // Pick the event_types actually present in the series to build the
  // dynamic line list. This keeps the chart uncluttered on new instances
  // where only a handful of events have fired.
  const activeSeries = useMemo(() => {
    const set = new Set();
    for (const row of series) {
      for (const k of Object.keys(row)) if (k !== "day") set.add(k);
    }
    // Preserve a stable order: known events (in EVENT_LABELS order) first,
    // then unknowns alphabetically.
    const knownOrder = Object.keys(EVENT_LABELS);
    const known = knownOrder.filter((k) => set.has(k));
    const rest = Array.from(set).filter((k) => !knownOrder.includes(k)).sort();
    return [...known, ...rest];
  }, [series]);

  const totalsRows = useMemo(() => {
    const totals = overview?.totals || {};
    return Object.entries(totals)
      .map(([k, v]) => ({ event: k, label: EVENT_LABELS[k] || k, count: v }))
      .sort((a, b) => b.count - a.count);
  }, [overview]);

  return (
    <section data-testid="usage-analytics">
      <div className="flex items-baseline justify-between flex-wrap gap-3 mb-4">
        <div>
          <div className="font-mono-cal text-[10px] uppercase tracking-widest text-[#666] mb-1">
            [ USAGE ANALYTICS ]
          </div>
          <h2 className="font-display font-bold text-2xl">
            {isPlatform ? "Platform activity" : "Your organization's activity"}
          </h2>
        </div>
        <div className="flex flex-wrap gap-2" data-testid="analytics-range-selector">
          {RANGES.map((r) => (
            <button
              key={r.key}
              onClick={() => setRange(r.key)}
              className={`font-mono-cal text-[10px] uppercase tracking-widest px-3 py-1.5 border ${
                range === r.key ? "bg-[#111] text-white border-[#111]" : "bg-white text-[#111] border-[#111]/20 hover:border-[#111]"
              }`}
              data-testid={`analytics-range-${r.key}`}
            >
              {r.label}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="font-mono-cal text-xs">[LOADING…]</div>
      ) : (
        <>
          {/* Top-line KPIs */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
            {isPlatform && (
              <StatCard label="ACTIVE ORGS" value={overview?.organizations_active_in_range}
                sublabel={`of ${overview?.organizations_total} total`} testid="stat-active-orgs"/>
            )}
            <StatCard label="MONTHLY ACTIVE USERS" value={overview?.mau}
              sublabel="last 30 days" testid="stat-mau"/>
            <StatCard label="WEEKLY ACTIVE USERS" value={overview?.wau}
              sublabel="last 7 days" testid="stat-wau"/>
            {isPlatform && (
              <StatCard label="DAILY ACTIVE USERS" value={overview?.dau}
                sublabel="last 24 hours" testid="stat-dau"/>
            )}
            {!isPlatform && (
              <StatCard label="LOGINS" value={overview?.totals?.login || 0}
                sublabel={`in the last ${range}`} testid="stat-org-logins"/>
            )}
          </div>

          {/* Feature usage totals in current range */}
          <div className="cal-card p-4 mb-6" data-testid="totals-card">
            <div className="font-mono-cal text-[10px] uppercase tracking-widest text-[#666] mb-3">
              [ FEATURE USAGE — LAST {range.toUpperCase()} ]
            </div>
            {totalsRows.length === 0 ? (
              <div className="text-sm text-[#666]">No activity in this range yet.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="totals-table">
                  <thead className="font-mono-cal text-[10px] uppercase tracking-widest text-[#666]">
                    <tr>
                      <th className="text-left p-2">Feature</th>
                      <th className="text-right p-2">Count</th>
                      <th className="text-right p-2 hidden sm:table-cell">Lifetime</th>
                    </tr>
                  </thead>
                  <tbody>
                    {totalsRows.map((r) => (
                      <tr key={r.event} className="border-t border-[#111]/10" data-testid={`totals-row-${r.event}`}>
                        <td className="p-2 font-display">{r.label}</td>
                        <td className="p-2 text-right font-mono-cal">{formatN(r.count)}</td>
                        <td className="p-2 text-right font-mono-cal text-[#666] hidden sm:table-cell">
                          {formatN(overview?.lifetime?.[r.event] || 0)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Time-series chart */}
          <div className="cal-card p-4 mb-6" data-testid="timeseries-card">
            <div className="font-mono-cal text-[10px] uppercase tracking-widest text-[#666] mb-3">
              [ ACTIVITY OVER TIME ]
            </div>
            {series.length === 0 ? (
              <div className="text-sm text-[#666] py-8 text-center">No data in this range yet.</div>
            ) : (
              <div style={{ width: "100%", height: 320 }}>
                <ResponsiveContainer>
                  <LineChart data={series} margin={{ top: 8, right: 16, left: -8, bottom: 4 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#eee"/>
                    <XAxis dataKey="day" tick={{ fontSize: 11 }} stroke="#666"/>
                    <YAxis tick={{ fontSize: 11 }} stroke="#666" allowDecimals={false}/>
                    <Tooltip contentStyle={{ fontSize: 12 }}/>
                    <Legend wrapperStyle={{ fontSize: 11 }}/>
                    {activeSeries.map((key, i) => (
                      <Line
                        key={key}
                        type="monotone"
                        dataKey={key}
                        name={EVENT_LABELS[key] || key}
                        stroke={SERIES_COLORS[i % SERIES_COLORS.length]}
                        strokeWidth={2}
                        dot={false}
                      />
                    ))}
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>

          {/* Top users */}
          <div className="cal-card p-4 mb-6" data-testid="top-users-card">
            <div className="font-mono-cal text-[10px] uppercase tracking-widest text-[#666] mb-3">
              [ TOP USERS — LAST {range.toUpperCase()} ]
            </div>
            {topUsers.length === 0 ? (
              <div className="text-sm text-[#666]">Nobody has been active in this range.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="font-mono-cal text-[10px] uppercase tracking-widest text-[#666]">
                    <tr>
                      <th className="text-left p-2">User</th>
                      <th className="text-right p-2">Events</th>
                      <th className="text-right p-2 hidden sm:table-cell">Last seen</th>
                    </tr>
                  </thead>
                  <tbody>
                    {topUsers.map((u) => (
                      <tr key={u.user_id} className="border-t border-[#111]/10" data-testid={`top-user-row-${u.user_id}`}>
                        <td className="p-2">
                          <div className="font-display font-semibold">{u.name || u.email || u.user_id}</div>
                          {u.email && <div className="text-xs text-[#666]">{u.email}</div>}
                        </td>
                        <td className="p-2 text-right font-mono-cal">{formatN(u.total)}</td>
                        <td className="p-2 text-right text-xs text-[#666] hidden sm:table-cell">{fmtDate(u.last_seen)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Platform-only sections: top orgs, inactive orgs, recent events */}
          {isPlatform && (
            <>
              <div className="grid md:grid-cols-2 gap-6 mb-6">
                <div className="cal-card p-4" data-testid="top-orgs-card">
                  <div className="font-mono-cal text-[10px] uppercase tracking-widest text-[#666] mb-3">
                    [ TOP ORGS — LAST {range.toUpperCase()} ]
                  </div>
                  {topOrgs.length === 0 ? (
                    <div className="text-sm text-[#666]">No org activity yet.</div>
                  ) : (
                    <div style={{ width: "100%", height: 260 }}>
                      <ResponsiveContainer>
                        <BarChart data={topOrgs} layout="vertical" margin={{ top: 4, right: 16, left: 8, bottom: 4 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#eee"/>
                          <XAxis type="number" tick={{ fontSize: 11 }} stroke="#666" allowDecimals={false}/>
                          <YAxis type="category" dataKey="org_name" tick={{ fontSize: 11 }} stroke="#666" width={120}/>
                          <Tooltip contentStyle={{ fontSize: 12 }}/>
                          <Bar dataKey="total" fill="#FF4F00" name="Events"/>
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  )}
                </div>

                <div className="cal-card p-4" data-testid="inactive-orgs-card">
                  <div className="font-mono-cal text-[10px] uppercase tracking-widest text-[#666] mb-3">
                    [ INACTIVE ORGS — NO ACTIVITY IN 30 DAYS ]
                  </div>
                  {inactive.length === 0 ? (
                    <div className="text-sm text-[#666]">🎉 Every org has been active in the last 30 days.</div>
                  ) : (
                    <div className="overflow-x-auto max-h-[260px] overflow-y-auto">
                      <table className="w-full text-sm">
                        <thead className="font-mono-cal text-[10px] uppercase tracking-widest text-[#666] sticky top-0 bg-white">
                          <tr>
                            <th className="text-left p-2">Org</th>
                            <th className="text-right p-2">Created</th>
                          </tr>
                        </thead>
                        <tbody>
                          {inactive.map((o) => (
                            <tr key={o.org_id} className="border-t border-[#111]/10" data-testid={`inactive-org-${o.org_id}`}>
                              <td className="p-2 font-display">{o.org_name}</td>
                              <td className="p-2 text-right text-xs text-[#666]">{fmtDate(o.created_at)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>

              <div className="cal-card p-4 mb-6" data-testid="recent-events-card">
                <div className="font-mono-cal text-[10px] uppercase tracking-widest text-[#666] mb-3">
                  [ RECENT EVENTS ]
                </div>
                {recent.length === 0 ? (
                  <div className="text-sm text-[#666]">No events recorded yet.</div>
                ) : (
                  <div className="overflow-x-auto max-h-[400px] overflow-y-auto">
                    <table className="w-full text-sm">
                      <thead className="font-mono-cal text-[10px] uppercase tracking-widest text-[#666] sticky top-0 bg-white">
                        <tr>
                          <th className="text-left p-2">When</th>
                          <th className="text-left p-2">Event</th>
                          <th className="text-left p-2">User</th>
                          <th className="text-left p-2 hidden sm:table-cell">Org</th>
                        </tr>
                      </thead>
                      <tbody>
                        {recent.map((e) => (
                          <tr key={e.id} className="border-t border-[#111]/10" data-testid={`recent-event-${e.id}`}>
                            <td className="p-2 text-xs text-[#666] whitespace-nowrap">{fmtDate(e.created_at)}</td>
                            <td className="p-2 font-mono-cal text-[11px]">{EVENT_LABELS[e.event_type] || e.event_type}</td>
                            <td className="p-2">
                              <div className="font-display text-sm">{e.user_name || e.user_email || "—"}</div>
                              {e.user_email && <div className="text-xs text-[#666]">{e.user_email}</div>}
                            </td>
                            <td className="p-2 text-xs text-[#666] hidden sm:table-cell">{e.org_name}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </>
          )}
        </>
      )}
    </section>
  );
}
